import { useState } from "react";
import { Sparkles, ArrowLeft, CheckCircle2, Camera, MessageCircleQuestion, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const TONES = [["warm", "Warm & personal"], ["luxury", "Refined & premium"], ["family", "Family-friendly"], ["adventurous", "Adventurous & outdoorsy"]];

const Q = ({ label, hint, value, onChange, testid, rows = 3 }) => (
  <div>
    <label className="block text-sm font-700 text-zinc-800">{label}</label>
    <p className="text-xs text-zinc-400 mb-1.5">{hint}</p>
    <textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600" />
  </div>
);

/** Guided interview -> AI drafts the full page -> owner reviews & applies. */
export const PageBuilderAI = ({ property, onClose, onApplied }) => {
  const [phase, setPhase] = useState("form"); // form | working | review
  const [a, setA] = useState({ story: "", ideal_guest: "", highlights: "", nearby: "", tone: "warm" });
  const [draft, setDraft] = useState(null);
  const [sel, setSel] = useState({ description: true, distinctive: true, ideal_guest: true, faq: true });

  const generate = async () => {
    setPhase("working");
    try {
      const { data } = await api.post(`/directstay/ai/page-builder/${property.id}`, a);
      setDraft(data.draft);
      setPhase("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI drafting failed — please try again");
      setPhase("form");
    }
  };

  const apply = async () => {
    const body = {};
    if (sel.description) body.description = draft.description;
    if (sel.distinctive) body.distinctive = draft.distinctive;
    if (sel.ideal_guest) body.ideal_guest = draft.ideal_guest;
    if (sel.faq) body.faq = draft.faq;
    try {
      await api.post(`/directstay/ai/page-builder/${property.id}/apply`, body);
      toast.success("Draft applied to your page");
      onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };

  const Check = ({ k, label }) => (
    <label className="inline-flex items-center gap-1.5 text-xs font-700 cursor-pointer">
      <input type="checkbox" data-testid={`builder-use-${k}`} checked={sel[k]} onChange={(e) => setSel({ ...sel, [k]: e.target.checked })} className="accent-emerald-700" /> {label}
    </label>
  );

  return (
    <div data-testid="page-builder" className="border border-zinc-300 bg-white p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 font-heading font-900 text-xl">
          <Sparkles className="w-5 h-5 text-amber-500" /> AI Page Builder — {property.name}
        </div>
        <button data-testid="builder-close" onClick={onClose} className="inline-flex items-center gap-1 text-xs font-600 text-zinc-500 hover:text-zinc-800"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
      </div>
      <p className="text-sm text-zinc-500 mb-5">Answer a few questions — the AI drafts your description, FAQ and photo plan. Nothing is published until you approve it.</p>

      {phase === "form" && (
        <div className="space-y-4">
          <Q testid="builder-story" label="1. Tell your story" hint="Who runs the property? How did it start? What's the atmosphere like?" value={a.story} onChange={(v) => setA({ ...a, story: v })} />
          <Q testid="builder-guest" label="2. Who is your ideal guest?" hint="Couples, families, business travelers, wellness seekers…" value={a.ideal_guest} onChange={(v) => setA({ ...a, ideal_guest: v })} rows={2} />
          <Q testid="builder-highlights" label="3. What should guests know about rooms, food & experiences?" hint="Signature rooms, home-cooked meals, backwater cruises, ayurveda…" value={a.highlights} onChange={(v) => setA({ ...a, highlights: v })} />
          <Q testid="builder-nearby" label="4. What's nearby and how do guests get to you?" hint="Attractions, distances, nearest airport or station, pickup options…" value={a.nearby} onChange={(v) => setA({ ...a, nearby: v })} rows={2} />
          <div>
            <label className="block text-sm font-700 text-zinc-800 mb-1.5">5. Tone of voice</label>
            <div className="flex flex-wrap gap-2">
              {TONES.map(([v, l]) => (
                <button key={v} data-testid={`builder-tone-${v}`} onClick={() => setA({ ...a, tone: v })}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-700 border ${a.tone === v ? "bg-emerald-700 text-white border-emerald-700" : "border-zinc-300 text-zinc-600 hover:border-emerald-600"}`}>{l}</button>
              ))}
            </div>
          </div>
          <button data-testid="builder-generate" onClick={generate} disabled={!a.story.trim() && !a.highlights.trim()}
            className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-700 px-6 py-3 text-sm uppercase tracking-wide">
            <Sparkles className="w-4 h-4" /> Draft my page
          </button>
        </div>
      )}

      {phase === "working" && (
        <div className="py-16 text-center text-zinc-500" data-testid="builder-working">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-emerald-700" />
          Writing your page draft — usually 15-30 seconds…
        </div>
      )}

      {phase === "review" && draft && (
        <div className="space-y-5" data-testid="builder-review">
          <div className="bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">Review the draft below. Untick anything you don't want, edit the description freely, then apply.</div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-700">Description</span><Check k="description" label="Use this" /></div>
            <textarea data-testid="builder-draft-description" rows={7} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-700">What makes it special</span><Check k="distinctive" label="Use this" /></div>
            <textarea rows={2} value={draft.distinctive} onChange={(e) => setDraft({ ...draft, distinctive: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-700">Ideal guest</span><Check k="ideal_guest" label="Use this" /></div>
            <textarea rows={2} value={draft.ideal_guest} onChange={(e) => setDraft({ ...draft, ideal_guest: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" />
          </div>
          {draft.taglines?.length > 0 && (
            <div>
              <span className="text-sm font-700">Tagline ideas</span>
              <div className="flex flex-wrap gap-2 mt-1.5">{draft.taglines.map((t, i) => <span key={i} className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-1">{t}</span>)}</div>
            </div>
          )}
          {draft.faq?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-700 inline-flex items-center gap-1.5"><MessageCircleQuestion className="w-4 h-4 text-emerald-700" /> FAQ ({draft.faq.length})</span><Check k="faq" label="Use these" /></div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {draft.faq.map((f, i) => (
                  <details key={i} className="border border-zinc-200 p-2.5 text-sm">
                    <summary className="font-600 cursor-pointer">{f.q}</summary>
                    <p className="text-zinc-600 mt-1.5">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}
          {draft.photo_shot_list?.length > 0 && (
            <div>
              <span className="text-sm font-700 inline-flex items-center gap-1.5"><Camera className="w-4 h-4 text-emerald-700" /> Photos worth uploading</span>
              <ul className="mt-1.5 space-y-1">{draft.photo_shot_list.map((s, i) => <li key={i} className="text-xs text-zinc-600 flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {s}</li>)}</ul>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button data-testid="builder-apply" onClick={apply} className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-700 px-6 py-3 text-sm uppercase tracking-wide"><CheckCircle2 className="w-4 h-4" /> Apply to my page</button>
            <button data-testid="builder-redo" onClick={() => setPhase("form")} className="border border-zinc-300 px-5 py-3 text-sm font-600 hover:border-emerald-600">Change answers</button>
          </div>
        </div>
      )}
    </div>
  );
};
